import { useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { useSelector } from 'react-redux'
import { changeUserEmail, firestore } from '../../../../../utils/firebase/firebase'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import {
  refreshMembershipStatus,
  addRestriction,
  removeRestriction,
  RESTRICTION_TIMEFRAME,
} from '../helpers'
import useIsUserRestricted from '../useIsUserRestricted'

const PersonalDetailsSection = ({ user }) => {
  const [clickedChangeDetails, setClickedChangeDetails] = useState(false)
  const [clickedChangeBrand, setClickedChangeBrand] = useState(false)
  const { isRestricted, friendlyRestrictionEnds } = useIsUserRestricted(user)
  const [newBrand, setNewBrand] = useState(user.brand)
  const [formData, setFormData] = useState({
    firstName: user.firstName,
    lastName: user.lastName,
    mobile: user.mobile,
    credits: user.credits,
  })
  const [clickedChangeEmail, setClickedChangeEmail] = useState(false)
  const [newEmail, setNewEmail] = useState(user.email)
  const [emailSaving, setEmailSaving] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [adminSaving, setAdminSaving] = useState(false)
  const currentUser = useSelector((s) => s.user.currentUser)
  const canManageAdmins =
    currentUser?.isSuperAdmin === true &&
    currentUser.id !== user.id &&
    !user.isSuperAdmin
  const userRef = firestore.collection('users').doc(user.id)
  const onChange = (e) => {
    setFormData((prevState) => ({
      ...prevState,
      [e.target.id]: e.target.value,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (clickedChangeDetails) {
      setClickedChangeDetails(false)
      try {
        await userRef
          .update({
            ...formData,
            credits: parseInt(formData.credits),
          })
          .then(() => {
            toast.success('User details updated')
          })
      } catch (error) {
        toast.error('Error updating user details')
      }
      return
    }
    setClickedChangeDetails(true)
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      mobile: user.mobile,
      credits: user.credits,
    })
  }

  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  const startEmailEdit = () => {
    setNewEmail(user.email)
    setClickedChangeEmail(true)
  }

  const cancelEmailEdit = () => {
    setClickedChangeEmail(false)
    setNewEmail(user.email)
  }

  const requestEmailChange = () => {
    const email = newEmail.trim().toLowerCase()
    if (!EMAIL_REGEX.test(email)) {
      toast.error('Enter a valid email address')
      return
    }
    if (email === (user.email || '').toLowerCase()) {
      cancelEmailEdit()
      return
    }
    setConfirmOpen(true)
  }

  const confirmEmailChange = async () => {
    const email = newEmail.trim().toLowerCase()
    setEmailSaving(true)
    try {
      const result = await changeUserEmail({ uid: user.id, newEmail: email })
      if (result && result.stripeSynced === false) {
        toast.warn('Email updated, but Stripe could not be synced — check Stripe.')
      } else {
        toast.success('Email updated')
      }
      setConfirmOpen(false)
      setClickedChangeEmail(false)
    } catch (error) {
      console.error(error)
      toast.error(error.message || 'Error updating email')
    } finally {
      setEmailSaving(false)
    }
  }

  const togglePt = async () => {
    try {
      await userRef
        .update({
          isPt: !user.isPt,
        })
        .then(() => {
          toast.success('User updated successfully')
        })
    } catch (error) {
      toast.error('Error updating user details')
    }
  }

  const toggleAdmin = async () => {
    setAdminSaving(true)
    try {
      await userRef.update({ isAdmin: !user.isAdmin })
      toast.success(user.isAdmin ? 'Admin access removed' : 'Admin access granted')
    } catch (error) {
      console.error(error)
      toast.error(error.message || 'Error updating admin access')
    } finally {
      setAdminSaving(false)
    }
  }

  const updateBrand = async (e) => {
    e.preventDefault()
    setClickedChangeBrand((prevState) => !prevState)

    if (clickedChangeBrand) {
      try {
        await userRef
          .update({
            brand: newBrand,
          })
          .then(() => {
            toast.success('User updated successfully')
          })
      } catch (error) {
        toast.error('Error updating user details')
      }
      return
    }
  }

  return (
    <Stack>
      <form onSubmit={handleSubmit}>
        <Box display={'flex'} justifyContent='space-between'>
          <Typography variant='h5' sx={{ color: 'white!important' }}>
            Personal details
          </Typography>
          <Box display={'flex'} gap={1}>
            <Button variant='contained' type='submit'>
              {clickedChangeDetails ? 'Submit' : 'Update'}
            </Button>
            {clickedChangeDetails && (
              <Button
                variant='outlined'
                onClick={() => setClickedChangeDetails(false)}
              >
                Cancel
              </Button>
            )}
          </Box>
        </Box>

        <Stack sx={{ mt: 2 }} gap={1.5}>
          <TextField
            id='firstName'
            label='First name'
            value={clickedChangeDetails ? formData.firstName : user.firstName}
            disabled={!clickedChangeDetails}
            onChange={onChange}
          />
          <TextField
            id='lastName'
            label='Surname'
            value={clickedChangeDetails ? formData.lastName : user.lastName}
            disabled={!clickedChangeDetails}
            onChange={onChange}
          />
          <TextField
            id='mobile'
            label='Mobile'
            value={clickedChangeDetails ? formData.mobile : user.mobile}
            disabled={!clickedChangeDetails}
            onChange={onChange}
          />
          <TextField
            id='credits'
            label='Credits'
            value={clickedChangeDetails ? formData.credits : user.credits}
            disabled={!clickedChangeDetails}
            onChange={onChange}
            type='number'
          />
          <TextField
            id='DOB '
            label='DOB'
            value={dayjs(user.dob).format('DD MMM YYYY') ?? 'unavailable'}
            disabled={true}
          />
        </Stack>
      </form>
      <Stack gap={1}>
        <Box display={'flex'} justifyContent='space-between' alignItems='center'>
          <Typography variant='h6' sx={{ color: 'white!important' }} mt={3}>
            Email
          </Typography>
          <Box display={'flex'} gap={1} mt={3}>
            {clickedChangeEmail ? (
              <>
                <Button variant='contained' onClick={requestEmailChange}>
                  Submit
                </Button>
                <Button variant='outlined' onClick={cancelEmailEdit}>
                  Cancel
                </Button>
              </>
            ) : (
              <Button variant='contained' onClick={startEmailEdit}>
                Change
              </Button>
            )}
          </Box>
        </Box>
        <TextField
          id='email'
          label='Email'
          value={clickedChangeEmail ? newEmail : user.email}
          disabled={!clickedChangeEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          fullWidth
        />
        {clickedChangeEmail && (
          <Typography variant='body2' fontWeight={'light'}>
            The member will be signed out and must log in with the new email.
            Their password is unchanged.
          </Typography>
        )}
      </Stack>
      <Stack gap={1}>
        <Typography variant='h6' sx={{ color: 'white!important' }} mt={3}>
          Membership Status
        </Typography>
        <Box display={'flex'} gap={1}>
          <TextField
            value={user.status}
            sx={{ textTransform: 'capitalize' }}
            disabled
            size='small'
            fullWidth
          />
          <Button
            variant='contained'
            onClick={() => refreshMembershipStatus(user.id, user.status)}
          >
            Refresh
          </Button>
        </Box>
      </Stack>

      <Stack gap={1}>
        <Typography variant='h6' sx={{ color: 'white!important' }} mt={3}>
          Personal Trainer
        </Typography>
        <Typography mb={1} variant='body2' fontWeight={'light'}>
          Enable a user as a personal trainer to make them available as a class
          host
        </Typography>
        <FormControlLabel
          control={
            <Switch checked={user.isPt} onChange={() => togglePt()} name='No' />
          }
          label={'PT'}
        />
        {user.isPt && (
          <Box
            display={'flex'}
            gap={1}
            component={'form'}
            onSubmit={updateBrand}
          >
            <TextField
              label='Custom brand name'
              fullWidth
              size='small'
              value={clickedChangeBrand ? newBrand : user.brand}
              onChange={(e) => setNewBrand(e.target.value)}
              disabled={!clickedChangeBrand}
            />
            <Button variant='contained' type='submit'>
              {clickedChangeBrand ? 'Submit' : 'Update'}
            </Button>
            {clickedChangeBrand && (
              <Button
                variant='outlined'
                onClick={() => setClickedChangeBrand(false)}
              >
                Cancel
              </Button>
            )}
          </Box>
        )}
      </Stack>
      {canManageAdmins && (
        <Stack gap={1}>
          <Typography variant='h6' sx={{ color: 'white!important' }} mt={3}>
            Admin Access
          </Typography>
          <Typography mb={1} variant='body2' fontWeight={'light'}>
            Give this user access to the admin panel
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={user.isAdmin === true}
                onChange={toggleAdmin}
                disabled={adminSaving}
              />
            }
            label={'Admin'}
          />
        </Stack>
      )}
      <Stack gap={1}>
        <Typography variant='h6' sx={{ color: 'white!important' }} mt={3}>
          Restrict User
        </Typography>
        <Typography mb={1} variant='body2' fontWeight={'light'}>
          {`Restrict the user from booking classes for ${RESTRICTION_TIMEFRAME} days.`}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={isRestricted}
              onChange={() =>
                isRestricted
                  ? removeRestriction(user.id)
                  : addRestriction(user.id)
              }
              name='Yes'
            />
          }
          label={'Restrict'}
        />
        {isRestricted && (
          <Box display={'flex'} gap={1} component={'form'}>
            <TextField
              label='Expires'
              fullWidth
              size='small'
              disabled
              value={`Restriction Expires: ${friendlyRestrictionEnds}`}
            />

            <Button variant='contained' onClick={() => addRestriction(user.id)}>
              Extend
            </Button>
          </Box>
        )}
      </Stack>
      <Dialog open={confirmOpen} onClose={() => !emailSaving && setConfirmOpen(false)}>
        <DialogTitle>Change member email?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This changes {user.firstName} {user.lastName}'s login email from{' '}
            <strong>{user.email}</strong> to{' '}
            <strong>{newEmail.trim().toLowerCase()}</strong> and signs them out of
            active sessions. They'll log in with the new email; their password is
            unchanged.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={emailSaving}>
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={confirmEmailChange}
            disabled={emailSaving}
          >
            {emailSaving ? 'Updating…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default PersonalDetailsSection
